import AIGradientIcon from 'assets/svg/icons/AIGradientIcon';
import classNames from 'classnames';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import { useIsFeatureEligible } from 'hooks/useIsFeatureEligible';
import { AnimatePresence } from 'motion/react';
import { useState, type JSX } from 'react';
import type {
  DocumentReferenceField,
  DocumentReferenceFieldTax,
} from 'types/repository.types';
import { UserFeatures, type IUser } from 'types/users.types';
import { ChevronDownIconOutline } from 'utils/icons';
import useDocumentFields from '../useDocumentFields';
import useTaxLink from '../useTaxLink';
import DocumentRepositoryTaxDebugField from './DocumentRepositoryTaxDebugField';
import DocumentRepositoryTaxField from './DocumentRepositoryTaxField';
import DocumentRepositoryTaxPaymentField from './DocumentRepositoryTaxPaymentField';
import DocumentRepositoryUserField from './DocumentRepositoryUserField';

type DocumentRepositoryFieldsProps = {
  documentId: string;
  setBoundingBoxField: (field: string | null) => void;
};

const DocumentRepositoryFieldRenderer = ({
  field,
  ...rest
}: DocumentRepositoryFieldsProps & {
  field: DocumentReferenceField;
}): JSX.Element | null => {
  switch (field.type) {
    case 'VATNumber':
    case 'VATRegime':
    case 'VATFrequency':
    case 'steuernummer':
      return <DocumentRepositoryUserField {...rest} field={field} />;
    case 'tax':
      return <DocumentRepositoryTaxField {...rest} field={field} />;
    case 'tax_payment_reminder':
    case 'tax_assessment':
      return <DocumentRepositoryTaxPaymentField {...rest} field={field} />;
    case 'tax_vat_account_statement':
    case 'tax_receipt':
    case 'tax_prepayment':
      return <DocumentRepositoryTaxDebugField {...rest} field={field} />;
    default:
      return null;
  }
};

const useFieldsFilter = (fields: DocumentReferenceField[] = []) => {
  const user = useGetCurrentCustomerData().data as IUser;

  const shouldSeeDebugFields = useIsFeatureEligible(
    UserFeatures.MINFIN_DOCUMENT_EXTRACTOR,
  );

  // AI suggested, generic tax field. Hidden in case the tax item is not found.
  // We can safely assume there must be at most one such field.
  const guessedTaxField = fields.find(
    (field) => field.type === 'tax',
  ) as DocumentReferenceFieldTax;

  const { taxId: guessedTaxId } = useTaxLink(
    guessedTaxField?.value?.period,
    guessedTaxField?.value?.type,
  );

  return fields?.filter((field) => {
    if (['accepted', 'rejected'].includes(field.status || '')) return false;

    // TODO: re-enable VATRegime when we implement the confirm dialog
    if (field.type === 'VATRegime') return field.status === 'pending';

    // only show fields that are not the same as the user's
    if (field.type === 'VATNumber') return user.VATNumber !== field.value;
    if (field.type === 'VATFrequency')
      return user.VATReturnFrequency !== field.value;
    if (field.type === 'steuernummer') return user.steuernummer !== field.value;

    if (
      ['tax_vat_account_statement', 'tax_receipt', 'tax_prepayment'].includes(
        field.type,
      )
    )
      return shouldSeeDebugFields;

    if (field.type === 'tax') return !!guessedTaxId;

    return true;
  });
};

const DocumentRepositoryFields = ({
  documentId,
  setBoundingBoxField,
}: DocumentRepositoryFieldsProps): JSX.Element | null => {
  const { data: documentFields } = useDocumentFields(documentId);

  const [isOpen, setIsOpen] = useState(true);

  const fields = useFieldsFilter(documentFields);

  if (!fields?.length) return null;

  return (
    <div
      className={classNames(
        'flex flex-col rounded-lg transition-all duration-300',
        !isOpen && 'ai-border-animated max-h-[44px]',
        isOpen && 'ai-border max-h-[800px]',
      )}
    >
      <div
        className="flex gap-2 p-2.5 rounded-lg items-center cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-center">
          <AIGradientIcon className="w-6 h-6" />
        </div>

        <h4 className="flex-1 text-sm">AI Suggestions</h4>

        <div className="flex items-center justify-center pt-0.5">
          <ChevronDownIconOutline className="w-5 h-5" />
        </div>
      </div>

      {isOpen && (
        <div
          className="flex flex-col gap-2 px-4 py-1 mb-2"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <AnimatePresence>
            {fields?.map((field) => (
              <DocumentRepositoryFieldRenderer
                key={field._id}
                field={field}
                documentId={documentId}
                setBoundingBoxField={setBoundingBoxField}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default DocumentRepositoryFields;
